import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Label, Popover, PopoverContent, PopoverTrigger, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useProgramDocuments } from '../../Docs/hooks';
/**
 * Combobox over a program's recorded documents (delivery.program_documents,
 * the system of record — includes docs auto-linked by extraction and docs
 * recorded by hand). Modeled on GovernmentOrgPicker's shape: a plain
 * controlled component (not wired into react-hook-form's Controller) meant to
 * be rendered through EntityFormDialog's `extraContent` escape hatch. The
 * value is the document's file artifact id (the cross-schema FK
 * ProgramDocument.file_id points at); the label is the document title with
 * its filename.
 */ export function ProgramDocumentPicker({ id, label, helpText, value, onChange, programId }) {
    const [open, setOpen] = useState(false);
    const { data, isLoading } = useProgramDocuments(programId || null);
    // Several document rows can reference the same artifact (e.g. a file
    // recorded once program-wide and once under a company) — offer each
    // artifact once, under the first row's title.
    const docs = (data?.items ?? []).filter((d, i, all)=>d.fileId && all.findIndex((o)=>o.fileId === d.fileId) === i);
    const selected = docs.find((d)=>d.fileId === value);
    const triggerLabel = selected ? selected.fileName || selected.title : 'Select a document…';
    const handleSelect = (artifactId)=>{
        onChange(artifactId);
        setOpen(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: id,
                children: label
            }),
            helpText && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: helpText
            }),
            /*#__PURE__*/ _jsxs(Popover, {
                open: open,
                onOpenChange: setOpen,
                children: [
                    /*#__PURE__*/ _jsx(PopoverTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            id: id,
                            type: "button",
                            variant: "secondary",
                            role: "combobox",
                            "aria-expanded": open,
                            disabled: !programId,
                            className: "h-10 w-full justify-between bg-background font-normal",
                            "data-testid": Testids.deliveryShared.fkCombobox,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate text-left",
                                    children: triggerLabel
                                }),
                                /*#__PURE__*/ _jsx(ChevronsUpDown, {
                                    className: "ml-2 h-4 w-4 shrink-0 opacity-50"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(PopoverContent, {
                        className: "w-[var(--radix-popover-trigger-width)] p-0",
                        align: "start",
                        children: /*#__PURE__*/ _jsxs(Command, {
                            label: `Search ${label}`,
                            children: [
                                /*#__PURE__*/ _jsx(CommandInput, {
                                    placeholder: "Search documents by name…",
                                    "data-testid": Testids.deliveryShared.fkComboboxSearch
                                }),
                                /*#__PURE__*/ _jsxs(CommandList, {
                                    children: [
                                        isLoading && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs",
                                            children: "Loading…"
                                        }),
                                        !isLoading && docs.length === 0 && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs text-muted-foreground",
                                            children: "No documents recorded on this program yet."
                                        }),
                                        !isLoading && docs.length > 0 && /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: docs.map((d)=>/*#__PURE__*/ _jsx(CommandItem, {
                                                    value: d.fileId,
                                                    keywords: [
                                                        d.title,
                                                        d.fileName
                                                    ],
                                                    "data-testid": Testids.deliveryShared.fkComboboxOption,
                                                    "data-row-id": d.fileId,
                                                    onSelect: ()=>handleSelect(d.fileId),
                                                    children: /*#__PURE__*/ _jsxs("span", {
                                                        className: "truncate",
                                                        children: [
                                                            d.fileName || d.title,
                                                            d.fileName && d.title && d.title !== d.fileName && /*#__PURE__*/ _jsxs("span", {
                                                                className: "text-muted-foreground",
                                                                children: [
                                                                    " — ",
                                                                    d.title
                                                                ]
                                                            })
                                                        ]
                                                    })
                                                }, d.id))
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
